import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';
import { formatINR, resolveImage } from '@/lib/format';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUSES = ['paid', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
    paid: 'bg-blue-100 text-blue-700',
    processing: 'bg-amber-100 text-amber-700',
    shipped: 'bg-purple-100 text-purple-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    created: 'bg-gray-100 text-gray-600',
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = () => { setLoading(true); api.get('/admin/orders').then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);

    const updateStatus = async (order, status) => {
        try {
            const { data } = await api.put(`/admin/orders/${order.id}/status`, { status });
            setOrders((os) => os.map((o) => o.id === order.id ? data : o));
            toast.success(`Order ${order.order_number} → ${status}`);
        } catch { toast.error('Update failed'); }
    };

    return (
        <div>
            <h1 className="font-display text-2xl lg:text-3xl font-bold">Orders</h1>
            <p className="text-[var(--ink-muted)] mt-1">{orders.length} orders</p>

            <div className="mt-6 rounded-2xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="py-24 grid place-items-center"><Loader2 className="h-7 w-7 animate-spin text-[var(--brand-blue)]" /></div>
                ) : orders.length === 0 ? (
                    <div className="py-20 text-center text-[var(--ink-muted)]">No orders yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table data-testid="admin-orders-table" className="w-full text-sm">
                            <thead><tr className="bg-[var(--surface-2)] text-left text-[var(--ink-muted)]">
                                <th className="px-4 py-3 font-medium">Order</th>
                                <th className="px-4 py-3 font-medium hidden md:table-cell">Customer</th>
                                <th className="px-4 py-3 font-medium hidden lg:table-cell">Date</th>
                                <th className="px-4 py-3 font-medium">Total</th>
                                <th className="px-4 py-3 font-medium hidden sm:table-cell">Payment</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr></thead>
                            <tbody>
                                {orders.map((o) => (
                                    <React.Fragment key={o.id}>
                                        <tr className="border-t border-border">
                                            <td className="px-4 py-3 font-semibold">{o.order_number}</td>
                                            <td className="px-4 py-3 hidden md:table-cell"><p className="font-medium">{o.customer_name}</p><p className="text-xs text-[var(--ink-muted)]">{o.email}</p></td>
                                            <td className="px-4 py-3 hidden lg:table-cell text-[var(--ink-muted)]">{(o.created_at || '').slice(0, 10)}</td>
                                            <td className="px-4 py-3 font-semibold">{formatINR(o.total)}</td>
                                            <td className="px-4 py-3 hidden sm:table-cell"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${o.payment?.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{o.payment?.status || 'pending'}</span></td>
                                            <td className="px-4 py-3">
                                                <Select value={o.status} onValueChange={(v) => updateStatus(o, v)}>
                                                    <SelectTrigger data-testid={`order-status-${o.order_number}`} className={`h-9 w-[140px] rounded-full text-xs font-semibold border-0 ${STATUS_COLORS[o.status] || ''}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                                </Select>
                                            </td>
                                            <td className="px-4 py-3"><button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[var(--surface-2)]">{expanded === o.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button></td>
                                        </tr>
                                        {expanded === o.id && (
                                            <tr className="bg-[var(--surface-2)]"><td colSpan={7} className="px-4 py-4">
                                                <div className="grid md:grid-cols-2 gap-6">
                                                    <div>
                                                        <p className="font-semibold mb-2">Items</p>
                                                        <div className="space-y-2">{o.items.map((it) => (<div key={it.product_id} className="flex items-center gap-3"><img src={resolveImage(it.image)} alt={it.name} loading="lazy" decoding="async" className="h-10 w-10 rounded-lg object-contain bg-white p-1" /><span className="flex-1">{it.name} × {it.qty}</span><span className="font-medium">{formatINR(it.line_total || it.price * it.qty)}</span></div>))}</div>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold mb-2">Shipping address</p>
                                                        <p className="text-[var(--ink)]">{o.shipping_address?.name} · {o.shipping_address?.phone}</p>
                                                        <p className="text-[var(--ink-muted)]">{[o.shipping_address?.line1, o.shipping_address?.line2, o.shipping_address?.landmark, o.shipping_address?.city, o.shipping_address?.state, o.shipping_address?.pincode].filter(Boolean).join(', ')}</p>
                                                    </div>
                                                </div>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
